import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { useControlCenter } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
  ModuleGatedCard,
} from '../components/portal-shell';
import {
  BandChip,
  type PerformanceLeaderboardResponse,
} from '../components/performance';
import { timeAgo } from '../lib/format';

export const Route = createFileRoute('/_authed/control-center')({
  component: ControlCenterPage,
});

const MODULE_LABELS: Record<string, string> = {
  module_exams: 'Exams & marks',
  module_assessment_engine: 'Tests & quizzes',
  module_wallet_referrals: 'Referrals & wallet',
  module_games: 'Games',
  module_hiring: 'Hiring',
  module_feedback: 'Parent feedback',
  module_tma: 'Teacher performance',
  module_teacher_reports: 'Teacher reports',
};

function ControlCenterPage() {
  return (
    <LandingGuard landingPath="/control-center">
      <ControlCenterContent />
    </LandingGuard>
  );
}

function ControlCenterContent() {
  const user = useAuthStore((s) => s.user);
  const jobsPath = useMenuPath('/system/scheduled-jobs');
  const query = useControlCenter({ enabled: !!user });
  // The TMA highlights card is purely additive on the Control Center —
  // we don't have a server-side highlight for super_admin yet (only
  // academic_admin gets it via the landings payload). Fetch directly
  // from the leaderboard endpoint; a 404 `feature_disabled` simply hides
  // the card behind the module-gated treatment.
  const tmaQuery = useQuery({
    queryKey: ['performance', 'leaderboard', { pageSize: 50 }] as const,
    enabled: Boolean(user),
    queryFn: ({ signal }) =>
      apiRequest<PerformanceLeaderboardResponse>(
        '/api/performance/leaderboard',
        { signal, query: { pageSize: 50, sortBy: 'score', sortOrder: 'desc' } },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title="Control Center"
        meta={user ? `${user.displayName} · Super Admin` : undefined}
        intro="System health, approvals, audit feed, and a quick map of every module."
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }

  if (query.isError || !query.data) {
    return (
      <LandingPage title="Control Center">
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data, role } = query.data;
  const flags = data.featureFlags;
  const isSupportViewer = role === 'support_viewer';
  const moduleEntries = Object.entries(data.moduleStatus);
  const hasModules = moduleEntries.length > 0;
  const hasAudit = data.recentAuditEvents.length > 0;
  const hasAutomations = data.automationStatusToday && data.automationStatusToday.length > 0;

  const statusTone =
    data.systemStatus === 'healthy'
      ? 'success'
      : data.systemStatus === 'degraded'
        ? 'warning'
        : 'danger';

  const pendingApprovalsValue =
    data.pendingApprovalsTotal === null ? '—' : data.pendingApprovalsTotal;
  const pendingApprovalsFoot = isSupportViewer
    ? 'Read-only access'
    : 'Across all modules';

  return (
    <LandingPage
      title="Control Center"
      meta={user ? `${user.displayName} · ${isSupportViewer ? 'Support Viewer' : 'Super Admin'}` : undefined}
      intro="System health, approvals, audit feed, and a quick map of every module."
    >
      <KPIRow>
        <KPICard
          label="System status"
          value={
            data.systemStatus === 'healthy'
              ? 'Healthy'
              : data.systemStatus === 'degraded'
                ? 'Degraded'
                : 'Down'
          }
          footnote={data.systemStatus === 'healthy' ? 'All services up' : 'Investigate'}
          tone={statusTone}
        />
        <KPICard
          label="Active users (24h)"
          value={data.activeUsers24h}
          footnote="Signed in over the last day"
        />
        <KPICard
          label="Pending approvals"
          value={pendingApprovalsValue}
          footnote={pendingApprovalsFoot}
        />
        <KPICard
          label="Features"
          value={flags.enabled + flags.disabled}
          footnote={`${flags.enabled} on · ${flags.disabled} off`}
          newFor="/system/feature-flags"
        />
      </KPIRow>

      {hasModules ? (
        <LandingCard title="Module status" newFor="/system/feature-flags" className="md:col-span-2">
          {moduleEntries.map(([key, entry], idx) => {
            const label = MODULE_LABELS[key] ?? key;
            const stateLabel = entry.enabled ? 'ON' : 'OFF';
            return (
              <LandingCardLine key={key} last={idx === moduleEntries.length - 1}>
                <span className="font-medium">{label}</span> ·{' '}
                <span
                  className={
                    entry.enabled
                      ? 'text-[color:var(--color-success)]'
                      : 'text-[color:var(--color-text-muted)]'
                  }
                >
                  {stateLabel}
                </span>
                {entry.healthHint ? (
                  <>
                    {' · '}
                    <span className="text-[color:var(--color-text-muted)]">
                      {entry.healthHint}
                    </span>
                  </>
                ) : null}
              </LandingCardLine>
            );
          })}
        </LandingCard>
      ) : (
        <ModuleGatedCard
          title="Module status"
          variant="full"
          description="No modules are configured yet. Module health appears here as soon as the new modules are enabled."
        />
      )}

      <CardsRow>
        <LandingCard title="Recent activity">
          {hasAudit ? (
            data.recentAuditEvents.map((event, idx) => (
              <LandingCardLine
                key={event.id}
                last={idx === data.recentAuditEvents.length - 1}
              >
                <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
                  {timeAgo(event.occurredAt)}
                </span>{' '}
                · {event.actorRole ?? 'system'} {event.action}
                {event.targetType ? (
                  <span className="text-[color:var(--color-text-muted)]">
                    {' '}
                    on {event.targetType}
                  </span>
                ) : null}
              </LandingCardLine>
            ))
          ) : (
            <EmptyState
              title="No recent events"
              description="Privileged writes show up here as they happen."
              className="py-6"
            />
          )}
        </LandingCard>

        {hasAutomations ? (
          <LandingCard title="Today's automation">
            {(data.automationStatusToday ?? []).map((run, idx, arr) => (
              <LandingCardLine key={`${run.task}-${idx}`} last={idx === arr.length - 1}>
                <span className="font-medium">{run.task}</span> ·{' '}
                <span
                  className={
                    run.status === 'ok'
                      ? 'text-[color:var(--color-success)]'
                      : run.status === 'error'
                        ? 'text-[color:var(--color-danger)]'
                        : 'text-[color:var(--color-text-muted)]'
                  }
                >
                  {run.status === 'ok' ? 'Done' : run.status === 'error' ? 'Problem' : 'Nothing to do'}
                </span>
                {' · '}
                <span className="text-[color:var(--color-text-muted)]">
                  {timeAgo(run.ranAt)}
                </span>
                {run.notes ? (
                  <span className="text-[color:var(--color-text-muted)]">
                    {' '}
                    — {run.notes}
                  </span>
                ) : null}
              </LandingCardLine>
            ))}
          </LandingCard>
        ) : (
          <ModuleGatedCard
            title="Today's automation"
            description={`Nothing has run yet today. Scheduled jobs (monthly fees, reminders and more) show here as they run${jobsPath ? ` — see ${jobsPath}` : ''}.`}
          />
        )}

        <TmaHighlightsCard query={tmaQuery} />
      </CardsRow>
    </LandingPage>
  );
}

interface TmaHighlightsCardProps {
  query: ReturnType<typeof useQuery<PerformanceLeaderboardResponse, ApiError | Error>>;
}

function TmaHighlightsCard({ query }: TmaHighlightsCardProps) {
  if (query.isLoading) {
    return (
      <LandingCard title="Teacher performance highlights" newFor="/hr/performance">
        <LandingCardLine last>Loading…</LandingCardLine>
      </LandingCard>
    );
  }
  // 404 `feature_disabled` means the TMA module is off — show the gated card.
  if (
    query.error instanceof ApiError &&
    query.error.status === 404 &&
    query.error.code === 'feature_disabled'
  ) {
    return (
      <ModuleGatedCard
        title="Teacher performance highlights"
        description="The TMA module runs nightly and surfaces top and bottom performers here when it's enabled."
      />
    );
  }
  if (query.error || !query.data) {
    return (
      <LandingCard title="Teacher performance highlights" newFor="/hr/performance">
        <LandingCardLine last>
          <span className="text-[color:var(--color-text-muted)]">
            Couldn't load highlights — try refreshing.
          </span>
        </LandingCardLine>
      </LandingCard>
    );
  }

  const items = query.data.items;
  if (items.length === 0) {
    return (
      <LandingCard
        title="Teacher performance highlights"
        newFor="/hr/performance"
        titleAction={
          <Link
            to="/hr/performance"
            className="text-xs font-medium text-gold-700 dark:text-gold-300 underline"
          >
            Open
          </Link>
        }
      >
        <LandingCardLine last>
          <span className="text-[color:var(--color-text-muted)]">
            No snapshots yet — highlights appear after the first TMA run.
          </span>
        </LandingCardLine>
      </LandingCard>
    );
  }

  const top = items.slice(0, 3);
  const bottom = items.length > 3 ? items.slice(-1) : [];
  const allRows = [...top, ...bottom];

  return (
    <LandingCard
      title="Teacher performance highlights"
      newFor="/hr/performance"
      titleAction={
        <Link
          to="/hr/performance"
          className="text-xs font-medium text-gold-700 dark:text-gold-300 underline"
        >
          Open
        </Link>
      }
    >
      {allRows.map((row, idx) => (
        <LandingCardLine key={row.teacherId} last={idx === allRows.length - 1}>
          <span className="inline-flex items-center gap-2">
            <BandChip band={row.band} size="sm" />
            <span className="font-medium">{row.teacherName ?? '—'}</span>
            <span className="text-[color:var(--color-text-muted)] tabular-nums">
              · {row.score}
            </span>
          </span>
        </LandingCardLine>
      ))}
    </LandingCard>
  );
}
